import { registerSW } from 'virtual:pwa-register';

/**
 * Injetado pelo Vite no build (ver `define` em packages/web/vite.shared.ts).
 * Declarado aqui dentro, e não num .d.ts do pacote: os tsconfig dos apps
 * só incluem a própria pasta `src`, então um ambiente global daqui nunca
 * seria carregado por eles.
 */
declare const __BUILD_ID__: string | undefined;

/**
 * Um retângulo cru, fora do React — o recarregamento que vem em seguida
 * apaga a árvore React inteira de qualquer forma, então não há por que
 * montar componente para algo que vive menos de um segundo. Mesmo
 * gradiente do splash, para o corte não saltar de cor.
 */
function coverBeforeReload(): void {
  if (document.getElementById('mobi-reload-cover')) return;
  const cover = document.createElement('div');
  cover.id = 'mobi-reload-cover';
  cover.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;' +
    'background:linear-gradient(165deg,var(--c-brand-soft),var(--c-brand) 55%,var(--c-brand-deep));';
  document.body.appendChild(cover);
}

/**
 * Mantém o app atualizado.
 *
 * `autoUpdate` sozinho não resolve no iPhone: reabrir o PWA pelo
 * multitarefa restaura a página em vez de navegar, e o navegador nem
 * chega a perguntar se existe versão nova. O app fica preso numa versão
 * antiga indefinidamente — e nenhum deploy chega em quem já instalou.
 *
 * Aqui a checagem é explícita: toda vez que o app volta para a frente, e
 * a cada poucos minutos com ele aberto.
 */
export function setupAutoUpdate(): void {
  const updateSW = registerSW({
    immediate: true,

    onNeedRefresh() {
      // Cobre a tela ANTES de recarregar. A checagem roda em segundo
      // plano — inclusive no meio do uso, com o mapa aberto — e sem isto
      // a pessoa via a tela antiga por um instante até o recarregamento
      // assumir; parecia a tela principal piscando antes do splash, não
      // o splash aparecendo primeiro como devia.
      coverBeforeReload();
      void updateSW(true);
    },

    onRegisteredSW(_url, registration) {
      if (!registration) return;

      const check = () => {
        if (document.visibilityState === 'visible') void registration.update();
      };

      document.addEventListener('visibilitychange', check);
      window.addEventListener('focus', check);
      // Rede volta depois de um túnel: momento natural para conferir.
      window.addEventListener('online', check);

      const timer = setInterval(check, 5 * 60_000);
      window.addEventListener('pagehide', () => clearInterval(timer));
    },
  });
}

/** Identificador do build, para saber qual versão está no aparelho. */
export const BUILD_ID: string =
  typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
